"use client";

import { useQuery } from "@tanstack/react-query";
import { Clapperboard } from "lucide-react";
import type { StoryAuthorType } from "@/db/schema/story";
import { useStoryViewer } from "@/features/stories/components/story-viewer/story-viewer-provider";
import { storyQueryFns } from "@/features/stories/queries/fns";
import { storyKeys } from "@/features/stories/queries/keys";
import { cn } from "@/lib/utils";

type StoryHighlightsRowProps = {
  ownerType: StoryAuthorType;
  ownerId: string;
  className?: string;
};

const HIGHLIGHT_SKELETON_KEYS = ["first", "second", "third"] as const;
type HighlightsResponse = Awaited<ReturnType<typeof storyQueryFns.highlights>>;
const EMPTY_HIGHLIGHTS_RESPONSE: HighlightsResponse = {
  highlights: [],
  canManage: false,
};

export function StoryHighlightsRow({
  ownerType,
  ownerId,
  className,
}: StoryHighlightsRowProps) {
  const { openHighlight } = useStoryViewer();
  const { data, isError, isLoading } = useQuery({
    queryKey: storyKeys.highlights(ownerType, ownerId),
    queryFn: () => storyQueryFns.highlights(ownerType, ownerId),
    placeholderData: EMPTY_HIGHLIGHTS_RESPONSE,
    staleTime: 60_000,
  });

  const highlights =
    data?.highlights.filter((highlight) => highlight.itemCount > 0) ?? [];

  if (isError || (!isLoading && highlights.length === 0)) return null;

  return (
    <section className={cn("space-y-2", className)}>
      <h3 className="text-xs font-bold tracking-wider text-text-tertiary uppercase">
        Highlights
      </h3>
      <div className="scrollbar-hide flex gap-3 overflow-x-auto pb-1">
        {isLoading
          ? HIGHLIGHT_SKELETON_KEYS.map((key) => (
              <div
                key={key}
                className="h-24 w-[72px] shrink-0 animate-pulse rounded-lg bg-surface-alt"
              />
            ))
          : highlights.map((highlight) => (
              <button
                key={highlight.id}
                type="button"
                className="group w-[72px] shrink-0 text-left"
                onClick={() =>
                  openHighlight({
                    highlightId: highlight.id,
                    title: highlight.title,
                    coverUrl: highlight.coverUrl,
                  })
                }
              >
                <span className="relative block aspect-[3/4] overflow-hidden rounded-lg border border-border bg-surface-alt shadow-sm transition group-active:scale-95">
                  {highlight.coverUrl ? (
                    // biome-ignore lint/performance/noImgElement: story highlight covers are already app media URLs.
                    <img
                      src={highlight.coverUrl}
                      alt={highlight.title}
                      className="h-full w-full object-cover"
                      draggable={false}
                    />
                  ) : (
                    <span className="flex h-full w-full items-center justify-center text-text-tertiary">
                      <Clapperboard className="h-5 w-5" />
                    </span>
                  )}
                  <span className="absolute inset-x-0 bottom-0 bg-linear-to-t from-black/60 to-transparent px-1.5 pt-5 pb-1">
                    <span className="block truncate text-[10px] font-semibold text-white">
                      {highlight.title}
                    </span>
                  </span>
                </span>
              </button>
            ))}
      </div>
    </section>
  );
}
